import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, Calendar, FileText, Check, ExternalLink, PlayCircle, ChevronDown, Circle, CircleDot, Clock, Gift, Copy, Send, Rocket } from "lucide-react";

import { useAuth, api } from "@/lib/auth"; // eslint-disable-line no-unused-vars
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArtworkPanel } from "@/components/noxn/ArtworkPanel";
import { AccountLayout } from "@/components/account/AccountLayout";

// DD/MM/YYYY across the customer portal.
const ddmmyyyy = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
        if (Number.isNaN(d.getTime())) return "—";
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    } catch (e) { return "—"; }
};

const PIPELINE_LABEL = {
    new: "In review",
    approved: "Approved",
    live: "Live",
    done: "Complete",
    cancelled: "Cancelled",
};
const PIPELINE_STYLE = {
    new: "border-white/20 text-white bg-white/5",
    approved: "border-sky-400/40 text-sky-300 bg-sky-400/10",
    live: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10",
    done: "border-slate-500/40 text-slate-300 bg-white/5",
    cancelled: "border-[#FF5A00]/50 text-[#FF5A00] bg-[#FF5A00]/10",
};

const money = (aud) => aud ? `$${Number(aud).toLocaleString("en-AU")}` : "—";
const moneyCents = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Portal() {
    usePageMeta({ title: "My campaigns · NOXN", description: "Track your NOXN campaigns and contracts." });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null); // expanded campaign id

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/portal/campaigns");
            setRows(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load your campaigns.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => ({
        total: rows.length,
        live: rows.filter((r) => r.pipeline_status === "live").length,
        upcoming: rows.filter((r) => ["new", "approved"].includes(r.pipeline_status)).length,
    }), [rows]);

    return (
        <AccountLayout title="My Campaign">
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00] mb-3">/ Campaigns</div>
                        <h1 className="font-display font-black tracking-tighter text-white text-3xl sm:text-4xl leading-[1]">
                            Your campaigns.
                        </h1>
                        <p className="mt-3 text-slate-400 text-sm max-w-xl">
                            Every booking you&apos;ve made with NOXN — status, screens on air and contract state, all in one place.
                        </p>
                    </div>
                    <Link
                        to="/book"
                        data-testid="portal-header-cta"
                        className="group inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-3 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors self-start"
                    >
                        <Rocket size={13} /> Book a screen
                        <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>

            {/* Stats */}
            {!loading && rows.length > 0 && (
                <div className="mt-8 grid grid-cols-3 gap-3">
                    {[
                        { k: "Total", v: stats.total },
                        { k: "Live", v: stats.live },
                        { k: "Upcoming", v: stats.upcoming },
                    ].map((c, i) => (
                        <div key={i} className="rounded-2xl p-5 bg-[#1B2E55]/60 border border-white/10">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ {c.k}</div>
                            <div className="mt-2 font-display font-black tracking-tighter text-3xl text-white">{c.v}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-8">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="animate-spin text-[#FF5A00]" size={22} />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center" data-testid="portal-empty">
                        <PlayCircle size={22} className="text-[#FF5A00] mx-auto" />
                        <div className="mt-3 text-white text-lg font-display font-black tracking-tight">No campaigns yet</div>
                        <p className="mt-2 text-sm text-slate-400 max-w-sm mx-auto">
                            Book your first billboard — takes about four minutes, no long emails.
                        </p>
                        <Link
                            to="/book"
                            data-testid="portal-empty-cta"
                            className="mt-6 inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors"
                        >
                            <Rocket size={13} /> Book a screen
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="portal-campaign-list">
                        {rows.map((c) => (
                            <CampaignRow key={c.id} c={c}
                                isOpen={open === c.id}
                                onToggle={() => setOpen(open === c.id ? null : c.id)} />
                        ))}
                    </div>
                )}
            </div>

            <ReferralCard />
            </motion.div>
        </AccountLayout>
    );
}

export const ReferralCard = () => {
    const [data, setData] = useState(null);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteNote, setInviteNote] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get("/portal/referral")
            .then(({ data }) => setData(data))
            .catch(() => {});
    }, []);

    const sendInvite = async (e) => {
        e.preventDefault();
        const email = inviteEmail.trim();
        if (!email) return;
        setSending(true);
        try {
            const { data: res } = await api.post("/portal/referral/invite", {
                emails: [email],
                note: inviteNote.trim() || null,
                origin_url: window.location.origin,
            });
            if (res.sent?.length) {
                toast.success(`Invite sent to ${res.sent[0]}.`);
                setInviteEmail("");
                setInviteNote("");
                setData((d) => d ? {
                    ...d,
                    invites: [{ email: res.sent[0], sent_at: new Date().toISOString() }, ...(d.invites || [])],
                } : d);
            } else {
                toast.error("Couldn't send the invite — please try again.");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't send the invite.");
        } finally {
            setSending(false);
        }
    };

    if (!data) return null;
    const shareUrl = `${window.location.origin}${data.share_path}`;

    return (
        <div className="mt-12 rounded-2xl border border-[#FF5A00]/30 bg-[#1B2E55]/60 p-6" data-testid="portal-referral-card">
            <div className="flex items-center gap-2 mb-2">
                <Gift size={14} className="text-[#FF5A00]" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Refer a business</div>
            </div>
            <div className="text-white font-display font-black tracking-tight text-xl">
                Give $250. Get $250.
            </div>
            <p className="mt-2 text-sm text-slate-400 max-w-2xl">
                Share your link — any business that books through it gets <span className="text-white">$250 off</span> their
                first campaign, and you earn a <span className="text-white">$250 credit toward your next invoice</span> once
                their campaign is paid.
            </p>
            <div className="mt-4 flex items-stretch max-w-xl">
                <input
                    readOnly
                    value={shareUrl}
                    data-testid="portal-referral-link"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-xs font-mono-tech min-w-0"
                />
                <button
                    type="button"
                    data-testid="portal-referral-copy"
                    onClick={() => {
                        navigator.clipboard?.writeText(shareUrl)
                            .then(() => toast.success("Referral link copied."))
                            .catch(() => toast.error("Could not copy — select the link manually."));
                    }}
                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 text-xs font-semibold uppercase tracking-widest shrink-0 transition-colors"
                >
                    <Copy size={12} /> Copy
                </button>
            </div>

            <form onSubmit={sendInvite} className="mt-4 max-w-xl" data-testid="portal-referral-invite-form">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">/ Or invite by email</div>
                <div className="flex items-stretch">
                    <input
                        type="email"
                        required
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="their-email@business.com.au"
                        data-testid="portal-referral-invite-email"
                        className="flex-1 bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-xs font-mono-tech min-w-0 placeholder:text-slate-600"
                    />
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="portal-referral-invite-send"
                        className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-4 text-xs font-semibold uppercase tracking-widest shrink-0 transition-colors"
                    >
                        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send invite
                    </button>
                </div>
                <input
                    type="text"
                    maxLength={400}
                    value={inviteNote}
                    onChange={(e) => setInviteNote(e.target.value)}
                    placeholder="Add a personal note (optional)"
                    data-testid="portal-referral-invite-note"
                    className="mt-2 w-full bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-xs min-w-0 placeholder:text-slate-600"
                />
                {(data.invites || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="portal-referral-invite-history">
                        {data.invites.slice(0, 6).map((inv, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 border border-white/10 px-2.5 py-1 text-[10px] font-mono-tech text-slate-400">
                                <Check size={10} className="text-emerald-400" /> {inv.email}
                                <span className="text-slate-600">{(inv.sent_at || "").slice(0, 10)}</span>
                            </span>
                        ))}
                    </div>
                )}
            </form>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
                <div className="border border-white/10 bg-[#0F1E3B]/50 p-4" data-testid="portal-referral-total">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ Credits earned</div>
                    <div className="mt-1 font-display font-black tracking-tighter text-2xl text-white">
                        {moneyCents(data.total_credit_cents)}
                    </div>
                </div>
                <div>
                    {data.credits.length === 0 ? (
                        <div className="text-xs text-slate-500 border border-white/5 p-4 h-full flex items-center" data-testid="portal-referral-empty">
                            No referrals yet — share your link to start earning credits.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {data.credits.map((c, i) => (
                                <li key={c.id || i} className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2 text-sm"
                                    data-testid={`portal-referral-credit-${i}`}>
                                    <div className="min-w-0">
                                        <span className="text-white truncate">{c.referred_business || "New business"}</span>
                                        <span className="ml-2 text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                                            {(c.created_at || "").slice(0, 10)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="font-mono-tech text-emerald-300">+{moneyCents(c.amount_cents)}</span>
                                        <span className="px-2 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border border-emerald-400/40 text-emerald-300">
                                            {c.status || "earned"}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

const CampaignRow = ({ c, isOpen, onToggle }) => {
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isOpen || detail) return;
        setBusy(true);
        api.get(`/portal/campaigns/${c.id}`)
            .then(({ data }) => setDetail(data))
            .catch(() => toast.error("Could not load details."))
            .finally(() => setBusy(false));
    }, [isOpen, detail, c.id]);

    const pipelineClass = PIPELINE_STYLE[c.pipeline_status] || PIPELINE_STYLE.new;

    return (
        <div className="rounded-2xl border border-white/10 bg-[#1B2E55]/50 hover:border-white/20 transition-colors overflow-hidden" data-testid={`portal-row-${c.id}`}>
            <button
                type="button"
                onClick={onToggle}
                data-testid={`portal-row-${c.id}-toggle`}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
            >
                <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{c.business_name || "Campaign"}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1.5"><Calendar size={11} className="text-slate-500" /> {ddmmyyyy(c.launch_date)}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={11} className="text-slate-500" /> {(c.cities?.[0] || "—")}{c.cities?.length > 1 ? ` +${c.cities.length - 1}` : ""}</span>
                        <span className="inline-flex items-center gap-1.5"><PlayCircle size={11} className="text-slate-500" /> {c.bookings_count} screen{c.bookings_count === 1 ? "" : "s"}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-slate-300 hidden sm:inline font-mono-tech">{money(c.budget_amount_aud)}</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-mono-tech uppercase tracking-widest border ${pipelineClass}`}>
                        {PIPELINE_LABEL[c.pipeline_status] || c.pipeline_status}
                    </span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.16,1,0.3,1] }}
                        className="overflow-hidden border-t border-white/10"
                    >
                        <div className="px-5 py-6">
                            {busy && !detail ? (
                                <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> Loading…</div>
                            ) : detail ? (
                                <CampaignDetailBody c={detail} />
                            ) : null}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const NEXT_ACTION = {
    new: { label: "Awaiting review", desc: "Our team is matching a screen and preparing your quote. No action needed from you.", tone: "amber" },
    approved: { label: "Sign your contract", desc: "Your booking is approved. Sign the contract to lock in the slot.", tone: "orange" },
    live: { label: "Live on screen", desc: "Your billboard is running now. Proof-of-play captured after each week.", tone: "emerald" },
    done: { label: "Delivered", desc: "Campaign complete. Play log and receipt are attached.", tone: "slate" },
    cancelled: { label: "Cancelled", desc: "This booking is cancelled. Refunds (if any) will follow.", tone: "red" },
};

const STAGES = [
    { key: "new",       label: "Received" },
    { key: "approved",  label: "Approved" },
    { key: "live",      label: "Live" },
    { key: "done",      label: "Delivered" },
];

const stageIdx = (s) => Math.max(0, STAGES.findIndex((x) => x.key === s));

const CampaignDetailBody = ({ c }) => {
    const goal = (c.campaign_goal || "").replace(/_/g, " ");
    const bookings = c.bookings || [];
    const status = c.pipeline_status || "new";
    const next = NEXT_ACTION[status] || NEXT_ACTION.new;
    const stageI = stageIdx(status === "cancelled" ? "new" : status);

    return (
        <div className="space-y-6" data-testid="portal-campaign-detail">
            {/* Progress stepper */}
            <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5" data-testid="portal-stepper">
                <div className="grid grid-cols-4 gap-3">
                    {STAGES.map((s, i) => {
                        const done = i < stageI || (status === "done" && s.key === "done");
                        const cur = i === stageI && status !== "cancelled";
                        return (
                            <div key={s.key} className="relative">
                                <div className="flex items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                        done || cur ? "bg-[#FF5A00]" : "bg-white/15"
                                    } ${cur ? "ring-4 ring-[#FF5A00]/20" : ""}`} />
                                    <span className={`text-[10px] font-mono-tech uppercase tracking-widest ${done || cur ? "text-white" : "text-slate-500"}`}>{s.label}</span>
                                </div>
                                {i < STAGES.length - 1 && (
                                    <div className="absolute top-[5px] left-[calc(2.5rem+4px)] right-0 h-px" style={{ backgroundColor: done ? "#FF5A00" : "rgba(255,255,255,0.10)" }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Dynamic what's next panel */}
            <div className="rounded-2xl border border-[#FF5A00]/25 bg-[#FF5A00]/[0.06] p-5 flex items-start gap-4" data-testid="portal-next-action">
                <span className="w-9 h-9 rounded-xl bg-[#FF5A00]/15 border border-[#FF5A00]/30 flex items-center justify-center shrink-0">
                    <PlayCircle size={16} className="text-[#FF5A00]" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-[#FF5A00] mb-1">/ What&apos;s next</div>
                    <div className="text-white text-base font-semibold">{next.label}</div>
                    <div className="text-[13px] text-slate-300 mt-1.5">{next.desc}</div>
                </div>
                <Link to="/book" className="hidden sm:inline-flex items-center gap-2 self-center rounded-full bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2 text-[10px] font-semibold uppercase tracking-widest transition-colors">
                    Book another <ArrowRight size={11} />
                </Link>
            </div>

            {/* Grid: Details + Sidebar cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {/* Campaign details card */}
                    <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5" data-testid="portal-details-card">
                        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-4">/ Campaign details</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                            <Info k="Goal" v={goal || "—"} />
                            <Info k="Category" v={c.form_category === "cat1" ? "Category 1" : c.form_category === "cat2" ? "Category 2" : "—"} />
                            <Info k="Launch" v={ddmmyyyy(c.launch_date)} />
                            <Info k="Budget" v={money(c.budget_amount_aud)} />
                            <Info k="Cities" v={(c.cities && c.cities.length) ? c.cities.join(", ") : "—"} />
                            <Info k="Booked" v={ddmmyyyy(c.created_at)} />
                        </div>
                    </div>

                    {/* Screens */}
                    <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5">
                        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-4">/ Screens</div>
                        {bookings.length === 0 ? (
                            <div className="text-xs text-slate-400" data-testid="portal-bookings-empty">
                                Screens will appear here once your run window is scheduled.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="portal-bookings-list">
                                {bookings.map((b) => (
                                    <li key={b.id} className="rounded-xl border border-white/8 bg-[#1B2E55]/40 p-3 flex items-center gap-3" data-testid={`portal-booking-${b.id}`}>
                                        {b.photo_url ? (
                                            <img src={b.photo_url} alt="" className="w-14 h-14 object-cover rounded-lg border border-white/10 shrink-0" />
                                        ) : (
                                            <div className="w-14 h-14 rounded-lg border border-white/10 bg-[#0F1E3B] shrink-0 flex items-center justify-center">
                                                <MapPin size={14} className="text-slate-500" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white text-sm truncate">{b.screen_name}</div>
                                            <div className="text-[11px] text-slate-500">{b.city} · {ddmmyyyy(b.start_date)} → {ddmmyyyy(b.end_date)}</div>
                                        </div>
                                        <span className={`inline-block px-2.5 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border rounded-full ${
                                            b.status === "confirmed" ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10" :
                                            b.status === "hold" ? "border-amber-400/40 text-amber-300 bg-amber-400/10" :
                                            b.status === "waitlist" ? "border-[#FF5A00]/50 text-[#FF5A00] bg-[#FF5A00]/10" :
                                            "border-white/10 text-slate-300 bg-white/5"
                                        }`}>
                                            {b.status}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Timeline */}
                    {(c.timeline && c.timeline.length > 0) && (
                        <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5" data-testid="portal-timeline">
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-4">/ Activity</div>
                            <ol className="relative border-l border-white/10 pl-5 space-y-3" data-testid="portal-timeline-list">
                                {c.timeline.map((ev, i) => {
                                    const isFuture = ev.future;
                                    const dotColor = isFuture
                                        ? "text-slate-500"
                                        : ev.kind === "cancelled" ? "text-[#FF5A00]"
                                            : ev.kind === "contract_signed" || ev.kind === "go_live" || ev.kind === "payment_received"
                                                ? "text-emerald-400" : "text-[#FF5A00]";
                                    const Icon = isFuture ? Circle : (ev.kind === "cancelled" ? Circle : CircleDot);
                                    return (
                                        <li key={`${ev.kind}-${i}`} className="relative" data-testid={`portal-timeline-event-${ev.kind}`}>
                                            <Icon size={12} className={`absolute -left-[26px] top-[3px] ${dotColor} bg-[#0F1E3B]`} />
                                            <div className="flex items-baseline justify-between gap-3">
                                                <div className={`text-sm ${isFuture ? "text-slate-400" : "text-white"}`}>
                                                    {ev.label}
                                                    {ev.note && <span className="text-slate-500 text-[11px] ml-2">· {ev.note}</span>}
                                                </div>
                                                <div className="text-[11px] text-slate-500 shrink-0 inline-flex items-center gap-1">
                                                    {isFuture && <Clock size={9} />} {ddmmyyyy(ev.at)}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </div>

                {/* Sidebar cards */}
                <div className="space-y-4">
                    <ArtworkPanel campaignId={c.id} canUpload />

                    {c.invoice ? (
                        <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5" data-testid="portal-invoice-card">
                            <div className="flex items-baseline justify-between mb-3">
                                <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">/ Payment</div>
                                <span className={`inline-block px-2.5 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border rounded-full ${
                                    c.invoice.payment_status === "paid" ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10" :
                                    c.invoice.payment_status === "failed" ? "border-[#FF5A00]/60 text-[#FF5A00] bg-[#FF5A00]/10" :
                                    "border-white/15 text-slate-300 bg-white/5"
                                }`}>
                                    {c.invoice.payment_status || "pending"}
                                </span>
                            </div>
                            <div className="text-xs space-y-1.5 text-slate-300">
                                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{moneyCents(c.invoice.amount_subtotal_cents)}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">GST</span><span>{moneyCents(c.invoice.amount_gst_cents)}</span></div>
                                <div className="flex justify-between text-white border-t border-white/10 pt-2 mt-2">
                                    <span className="font-medium">Total</span>
                                    <span className="text-[#FF5A00] font-semibold">{moneyCents(c.invoice.amount_total_cents)}</span>
                                </div>
                            </div>
                            {c.invoice.paid_at && (
                                <div className="mt-3 text-[10px] font-mono-tech uppercase tracking-widest text-emerald-300">
                                    Paid {ddmmyyyy(c.invoice.paid_at)}
                                </div>
                            )}
                            {c.invoice.receipt_url && (
                                <a href={c.invoice.receipt_url} target="_blank" rel="noopener noreferrer"
                                    data-testid="portal-invoice-receipt"
                                    className="mt-4 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white px-3 py-2 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors w-full">
                                    Stripe receipt <ExternalLink size={10} />
                                </a>
                            )}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5 text-xs text-slate-400" data-testid="portal-invoice-pending">
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-2">/ Payment</div>
                            Payment link will appear here once your quote is confirmed.
                        </div>
                    )}

                    {c.contract ? (
                        <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5" data-testid="portal-contract-card">
                            <div className="flex items-baseline justify-between mb-3">
                                <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">/ Contract</div>
                                <span className={`inline-block px-2.5 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border rounded-full ${
                                    c.contract.status === "signed" ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10" :
                                    c.contract.status === "sent" ? "border-sky-400/50 text-sky-300 bg-sky-400/10" :
                                    "border-[#FF5A00]/50 text-[#FF5A00] bg-[#FF5A00]/10"
                                }`}>
                                    {c.contract.status === "signed" ? <>Signed <Check size={10} className="inline" /></> : c.contract.status}
                                </span>
                            </div>
                            <div className="text-white text-sm font-medium">{c.contract.contract_number}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">Total {moneyCents(c.contract.total_cents)}</div>
                            <div className="pt-3 flex flex-col gap-2">
                                <a href={`/portal/contract/${c.contract.access_token}`} target="_blank" rel="noopener noreferrer"
                                    data-testid="portal-contract-open"
                                    className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-3 py-2 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors">
                                    <FileText size={12} /> {c.contract.status === "signed" ? "View contract" : "Review & sign"} <ExternalLink size={10} />
                                </a>
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/public/${c.contract.access_token}/pdf`}
                                    target="_blank" rel="noopener noreferrer"
                                    data-testid="portal-contract-pdf"
                                    className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white px-3 py-2 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors">
                                    Download PDF <ArrowRight size={10} />
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-white/10 bg-[#0F1E3B]/40 p-5 text-xs text-slate-400" data-testid="portal-contract-pending">
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-2">/ Contract</div>
                            Your contract will appear here once the NOXN team approves the campaign.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Info = ({ k, v }) => (
    <div>
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">{k}</div>
        <div className="text-white text-sm mt-1 truncate">{v || "—"}</div>
    </div>
);
